import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable, take } from 'rxjs';
import { IORD_Order, MarketingSheetCutterDto } from '../../../interfaces/request.interface';
import { RTAApiClient } from '../../../services/rta-api.service';
import { GlobalNotificationService } from '../../../services/globalnotification.service';
import { Store } from '@ngxs/store';
import { RequestActions } from '../../../store/action/request.action';

declare var CURRENT_USER_ID: number;

@Component({
    selector: 'app-marketingsheet-data',
    templateUrl: './marketingsheet-data.component.html',
    styleUrl: './marketingsheet-data.component.css',
    standalone: false
})
export class MarketingsheetDataComponent implements OnInit {
  @Input() draftId: number = 0;
  @Input() request$!: Observable<IORD_Order | undefined>;
  @Input() bitFeatureIds: number[] = [];

  form: FormGroup;
  cutterSizes = ['8mm', '10mm', '13mm', '16mm', '19mm', '25mm'];
  materialNumber: string = '';
  subPsl: { name: string } = { name: '' };

  constructor(
    private fb: FormBuilder,
    private service: RTAApiClient,
    private globalNotificationService: GlobalNotificationService,
    private store: Store
  ) {
    this.form = this.fb.group({
      face8mm: [0],
      face10mm: [0],
      face13mm: [0],
      face16mm: [0],
      face19mm: [0],
      face25mm: [0],
      gauge8mm: [0],
      gauge10mm: [0],
      gauge13mm: [0],
      gauge16mm: [0],
      gauge19mm: [0],
      gauge25mm: [0]
    });
  }

  ngOnInit(): void {
    this.request$.pipe(take(1)).subscribe(order => {
      console.log('Loaded order for marketing sheet:', order);
      if (order) {
        if (order.subPsls?.name) {
          this.subPsl = { name: order.subPsls.name };
        }
        const bitMaterial = order.sapNumbers?.find(s => s.sapNumTypeId === 3);
        if (bitMaterial?.number) {
          this.materialNumber = bitMaterial.number.toString();
        }

        // Load saved marketing cutters
        if (this.draftId) {
          this.service.getMarketingCutters(this.draftId).subscribe({
            next: (cutters) => {
              if (cutters) {
                this.form.patchValue({
                  face8mm: cutters.c8f ?? 0,
                  face10mm: cutters.c10f ?? 0,
                  face13mm: cutters.c13f ?? 0,
                  face16mm: cutters.c16f ?? 0,
                  face19mm: cutters.c19f ?? 0,
                  face25mm: cutters.c25f ?? 0,
                  gauge8mm: cutters.c8g ?? 0,
                  gauge10mm: cutters.c10g ?? 0,
                  gauge13mm: cutters.c13g ?? 0,
                  gauge16mm: cutters.c16g ?? 0,
                  gauge19mm: cutters.c19g ?? 0,
                  gauge25mm: cutters.c25g ?? 0
                }, { emitEvent: false });
              }
            },
            error: (err) => console.error('Error loading marketing cutters:', err)
          });
        }
      }
    });
  }

  public saveMarketingSheet(): void {
    this.request$.pipe(take(1)).subscribe(order => {
      if (!order || !order.sapNumbers || order.sapNumbers.length === 0) {
        this.globalNotificationService.showErrorNotification('SAP numbers not found. Please generate SAP data first.');
        return;
      }

      const bitMaterial = order.sapNumbers.find(s => s.sapNumTypeId === 3);
      if (!bitMaterial || !bitMaterial.number) {
        this.globalNotificationService.showErrorNotification('Material number (SAP Type 3) not found. Please generate SAP data first.');
        return;
      }

      const formValues = this.form.value;
      const dto: MarketingSheetCutterDto = {
        matNum: bitMaterial.number,
        c8f: formValues.face8mm ?? 0,
        c8g: formValues.gauge8mm ?? 0,
        c10f: formValues.face10mm ?? 0,
        c10g: formValues.gauge10mm ?? 0,
        c13f: formValues.face13mm ?? 0,
        c13g: formValues.gauge13mm ?? 0,
        c16f: formValues.face16mm ?? 0,
        c16g: formValues.gauge16mm ?? 0,
        c19f: formValues.face19mm ?? 0,
        c19g: formValues.gauge19mm ?? 0,
        c25f: formValues.face25mm ?? 0,
        c25g: formValues.gauge25mm ?? 0
      };

      this.globalNotificationService.showInfoNotification('Saving marketing sheet...');

      // Save bit features via NGXS store (same as edit component save)
      this.store.dispatch(new RequestActions.SaveDraft({ draftId: this.draftId, userId: CURRENT_USER_ID }));

      this.service.saveMarketingSheetCutters(dto, this.draftId).subscribe({
        next: (response) => {
          if (response.status) {
            this.globalNotificationService.showSuccessNotification(
              response.message || 'Marketing sheet cutters saved successfully.'
            );
          } else {
            this.globalNotificationService.showErrorNotification(
              response.message || 'Failed to save marketing sheet cutters.'
            );
          }
        },
        error: (error) => {
          this.globalNotificationService.showErrorNotification(
            error.message || 'An error occurred while saving marketing sheet cutters.'
          );
          console.error('Error saving marketing sheet cutters:', error);
        }
      });
    });
  }
  onPhotosUploaded(): void {
  console.log('All marketing images uploaded successfully!');
}
}
